import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Reveal from "./Reveal";
import { Clock3, Users2, ShoppingBag, Workflow, Coins } from "lucide-react";

/**
 * MetricsStrip — five "core" floating metric cards (Clientes, Ventas, Procesos,
 * Tiempo, Margen). Each card drifts on scroll with a subtle parallax to give
 * the 3D / depth feeling without being heavy.
 */
const METRICS = [
  { icon: Clock3, label: "Tiempo", note: "¿En qué se va el día?" },
  { icon: ShoppingBag, label: "Ventas", note: "¿Qué entra de verdad?" },
  { icon: Coins, label: "Margen", note: "¿Cuánto queda al final?" },
  { icon: Users2, label: "Clientes", note: "¿Quién repite y por qué?" },
  { icon: Workflow, label: "Procesos", note: "¿Qué tienes sin sistema?" },
];

const MetricsStrip = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Different parallax offsets per card → floating depth
  const y0 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y1 = useTransform(scrollYProgress, [0, 1], [10, -10]);
  const y2 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, 0]);
  const y4 = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const ys = [y0, y1, y2, y3, y4];

  return (
    <section
      ref={ref}
      data-testid="metrics-strip"
      className="relative pt-2 pb-12 md:pb-16"
    >
      <div className="et-container relative z-10">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-[#c5a059] text-center mb-6">
            Lo que mide la Radiografía
          </p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              style={{ y: ys[i] }}
              data-testid={`metric-${m.label.toLowerCase()}`}
            >
              <Reveal delay={i * 0.06}>
                <div className="et-card !p-4 md:!p-5 h-full flex flex-col items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-lg grid place-items-center"
                    style={{
                      background: "rgba(197,160,89,0.1)",
                      border: "1px solid rgba(197,160,89,0.3)",
                    }}
                  >
                    <m.icon className="w-4 h-4 text-[#c5a059]" />
                  </div>
                  <div>
                    <p className="text-base md:text-lg font-semibold text-white tracking-tight leading-tight">
                      {m.label}
                    </p>
                    <p className="text-xs md:text-sm text-[#f5f2ea]/75 mt-1 leading-snug">
                      {m.note}
                    </p>
                  </div>
                </div>
              </Reveal>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MetricsStrip;
